import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";

const ACCENT_SPINNER = { indigo: "text-indigo-400", teal: "text-teal-400", fuchsia: "text-fuchsia-400" };

/** Renders a short AI-avatar video clip (HeyGen) speaking the current interview question.
 * Generation takes real render time (often 1-2 min), so this best-effort-shows-when-ready —
 * it never blocks the interview flow and silently hides itself if generation fails. */
export default function AvatarQuestionPlayer({ questionText, accent = "indigo" }) {
  const [status, setStatus] = useState("processing");
  const [videoUrl, setVideoUrl] = useState(null);
  const pollRef = useRef(null);
  const requestedForRef = useRef(null);

  useEffect(() => {
    if (!questionText || requestedForRef.current === questionText) return;
    requestedForRef.current = questionText;
    setStatus("processing");
    setVideoUrl(null);
    if (pollRef.current) clearInterval(pollRef.current);

    const pollStatus = (clipId) => {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/avatar-video/${clipId}/status`);
          setStatus(data.status);
          if (data.status === "completed") {
            setVideoUrl(data.video_url);
            clearInterval(pollRef.current);
          } else if (data.status === "failed") {
            clearInterval(pollRef.current);
          }
        } catch (e) {
          // network hiccup — retry on the next tick
        }
      }, 4000);
    };

    api.post("/avatar-video/generate", { question_text: questionText }).then(({ data }) => {
      setStatus(data.status);
      if (data.video_url) setVideoUrl(data.video_url);
      else if (data.status !== "failed") pollStatus(data.clip_id);
    }).catch(() => setStatus("failed"));

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [questionText]);

  if (!questionText || status === "failed") return null;

  const spinnerColor = ACCENT_SPINNER[accent] || ACCENT_SPINNER.indigo;

  return (
    <div className="mb-4 flex justify-center" data-testid="avatar-question-player">
      {videoUrl ? (
        <video
          key={videoUrl} src={videoUrl} autoPlay controls playsInline
          data-testid="avatar-question-video"
          className="w-52 rounded-xl shadow-lg border border-white/10"
        />
      ) : (
        <div className="w-52 aspect-square rounded-xl border border-white/10 bg-white/[0.03] flex flex-col items-center justify-center gap-2 p-4" data-testid="avatar-question-loading">
          <Loader2 className={`w-6 h-6 animate-spin ${spinnerColor}`} />
          <p className="text-[11px] text-slate-500 text-center">AI interviewer avatar is preparing…</p>
        </div>
      )}
    </div>
  );
}
